import {
  useWSSnackbar,
  WSActions,
  WSElement,
  WSForm,
  WSInfoBox,
  WSList,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { EngagementStatus } from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import {
  useQueryIntegrationsQuickbooks,
  useQueryIntegrationsQuickbooksAccountExpenses,
  useQueryIntegrationsQuickbooksVendors
} from "../../../../query/integrations/queries";
import { queriesEngagements, queriesPayees } from "@query";
import { selectorPayeeDisplayName } from "../../../../shared/selectors/selectorPayeeDisplayName";
import { useXWingspanPayerId } from "../../routes/RoutePayeeDetails/useXWingspanPayerId";
import { useMutationCreatePayeeEngagement } from "../../../../query/payeeEngagements/mutations/useMutationCreatePayeeEngagement";
import { useQueryPayeeEngagementsListByPayeeId } from "../../../../query/payeeEngagements/queries/useQueryPayeeEngagementsListByPayeeId";
import { FormPartialPayeeSearchField } from "../../../../shared/components/PayeeSearchField";
import { cleanOptionalFormValues } from "../../../../shared/utils/cleanOptionalFormValues";
import {
  FormPartialPayeeQboMapping,
  PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES
} from "../../../Integrations/components/FormPartialPayeeQboMapping";
import { selectorIsPayeeQboMappingEnabled } from "../../../Integrations/selectors/selectorIsPayeeQboMappingEnabled";
import { useAssignEngagementsBulkAction } from "../../hooks/useAssignEngagementsBulkAction";

export type CreateEngagementModalProps = {
  onClose(result: boolean): void;
  payeeId?: string;
  engagementId?: string;
};

type FormData = {
  payeeId: string;
  engagementIds: string[];
  qboVendorId: string;
  qboExpenseAccountId: string;
};

export const CreateEngagementModal: React.FC<CreateEngagementModalProps> = ({
  payeeId,
  engagementId,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const xWingspanAccount = useXWingspanPayerId();

  const queryEngagementsTypes = queriesEngagements.listEngagements.useQueryAll(
    {
      xWingspanAccount,
      filter: { status: { anyOf: [EngagementStatus.ACTIVE] } }
    },
    { enabled: !!payeeId }
  );

  const queryPayeeEngagements = useQueryPayeeEngagementsListByPayeeId(
    payeeId!,
    {},
    {
      enabled: !!payeeId
    }
  );

  const queryPayee = queriesPayees.getPayee.useQuery(
    { payeeId: payeeId!, xWingspanAccount },
    { enabled: !!payeeId }
  );

  const queryEngagement = queriesEngagements.getEngagement.useQuery(
    { engagementId: engagementId!, xWingspanAccount },
    { enabled: !!engagementId }
  );

  const queryFeatureFlags = useFeatureFlags();
  const queryIntegrationsQuickbooks = useQueryIntegrationsQuickbooks();
  const queryIntegrationsQuickbooksVendors =
    useQueryIntegrationsQuickbooksVendors();
  const queryIntegrationsQuickbooksExpenseAccounts =
    useQueryIntegrationsQuickbooksAccountExpenses();

  const isQboMappingEnabled = selectorIsPayeeQboMappingEnabled({
    expenseAccounts: queryIntegrationsQuickbooksExpenseAccounts.data,
    featureFlags: queryFeatureFlags?.data,
    qboService: queryIntegrationsQuickbooks.data,
    vendors: queryIntegrationsQuickbooksVendors.data
  });

  const [createEngagement, createEngagementMeta] =
    useMutationCreatePayeeEngagement();

  const runAssignEngagementsBulkAction = useAssignEngagementsBulkAction();

  const engagementsOptions = useMemo(
    () =>
      queryEngagementsTypes.flatData
        ?.filter(
          engagement =>
            !engagement.isDefault &&
            !queryPayeeEngagements.data?.some(
              eng => eng.engagementId === engagement.id
            )
        )
        .map(engagement => ({
          label: engagement.name ?? "",
          value: engagement.id ?? ""
        })) || [],
    [queryEngagementsTypes.flatData, queryPayeeEngagements.data]
  );

  const payeeName = queryPayee.data
    ? selectorPayeeDisplayName(queryPayee.data)
    : "";

  const handleSubmit = useCallback(
    async (data: FormData) => {
      const engagementIds = data.engagementIds || [];

      if (engagementIds.length === 1) {
        const qboPayload =
          isQboMappingEnabled && (data.qboVendorId || data.qboExpenseAccountId)
            ? cleanOptionalFormValues({
                quickbooks: {
                  expenseAccountId: data.qboExpenseAccountId,
                  vendorId: data.qboVendorId
                }
              })
            : undefined;

        await createEngagement(
          {
            engagementId: engagementIds[0],
            payeeId: data.payeeId,
            payerOwnedData: qboPayload ? { integration: qboPayload } : {}
          },
          { throwOnError: true }
        );

        onClose(true);
        openSnackbar({
          message: "Assignment added successfully",
          type: "success"
        });
      } else {
        const result = await runAssignEngagementsBulkAction(
          engagementIds.map(id => ({
            engagementId: id,
            name: engagementsOptions.find(o => o.value === id)?.label,
            payeeId: data.payeeId
          }))
        );
        if (!result) return;

        onClose(result);
      }
    },
    [
      createEngagement,
      engagementsOptions,
      isQboMappingEnabled,
      onClose,
      openSnackbar,
      runAssignEngagementsBulkAction
    ]
  );

  const validationSchema = Yup.object().shape({
    engagementIds: Yup.array()
      .of(Yup.string())
      .min(1, "At least one engagement is required")
      .required("At least one engagement is required"),
    payeeId: Yup.string().required("Contractor is required!")
  });

  return (
    <WSElement
      shimmer={
        payeeId
          ? queryPayee.isLoading ||
            queryEngagementsTypes.isLoading ||
            queryPayeeEngagements.isLoading
          : queryEngagement.isLoading
      }
    >
      <WSForm<FormData>
        defaultValues={{
          ...PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES,
          engagementIds: engagementId ? [engagementId] : [],
          payeeId: payeeId || ""
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <WSList gap="XL">
          {payeeId ? (
            <WSForm.Field
              name="payeeId"
              label="Contractor"
              component={WSSelect}
              componentProps={{
                disabled: true,
                options: [{ value: payeeId || "", label: payeeName }]
              }}
            />
          ) : (
            <FormPartialPayeeSearchField
              name="payeeId"
              engagementId={engagementId!}
            />
          )}
          {engagementId ? (
            <WSForm.Field
              name="engagementIds"
              label="Engagement"
              component={WSSelect}
              componentProps={{
                disabled: true,
                mode: "multiple",
                options: [
                  {
                    label: queryEngagement.data?.name || "Current engagement",
                    value: engagementId
                  }
                ]
              }}
            />
          ) : engagementsOptions.length > 0 ? (
            <WSForm.Field
              name="engagementIds"
              label="Engagements"
              component={WSSelect}
              componentProps={{
                mode: "multiple",
                options: engagementsOptions,
                placeholder: "Select engagements",
                required: true
              }}
            />
          ) : (
            <WSText.ParagraphSm align="center">
              No engagements available
            </WSText.ParagraphSm>
          )}

          <WSForm.Value name="engagementIds">
            {(engagementIds: string[] | null) => (
              <FormPartialPayeeQboMapping
                disabled={(engagementIds || [])?.length > 1}
              />
            )}
          </WSForm.Value>

          <WSInfoBox title="About assignments">
            Assignments appear on the contractor's profile. They must complete
            any assignment-specific requirements to receive payments. You can
            archive or restore assignments at any time.
          </WSInfoBox>

          <WSErrorMessage
            contextKey="Engagement"
            error={createEngagementMeta.error}
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Primary",
                label: "Add assignments",
                loading: createEngagementMeta.isLoading,
                type: "submit"
              },
              {
                kind: "Secondary",
                label: "Cancel",
                onClick() {
                  onClose(false);
                },
                type: "button"
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSElement>
  );
};
